import type { TypedSupabaseClient } from '$lib/supabase'
import type { FriendWithProfile } from '$lib/types'
import { get, writable } from 'svelte/store'
import type { Reloadable } from './reloadable'
import { untrack } from 'svelte'

type ActionResponse = { error?: string }

export type FriendsStore = Reloadable<FriendWithProfile[]> & {
  remove: (friendUid: string) => Promise<ActionResponse>
  add: (friendUid: string) => Promise<ActionResponse>
  switchNotifications: (
    friendUid: string,
    value?: boolean,
  ) => Promise<ActionResponse>
  /** Makes sure the friend list is loaded, and returns it. */
  get: () => Promise<FriendWithProfile[]>
}

export const createFriendsStore = (
  dependencies: {
    supabase: TypedSupabaseClient
    userId: string | null
  },
  reloadNewAchievements: () => void,
): FriendsStore => {
  let initialized = false

  const reload = async (): Promise<FriendWithProfile[]> => {
    if (!dependencies.userId) {
      _friends.set([])
      return []
    }
    const loadedFriends = await _loadFriends(
      dependencies.supabase,
      dependencies.userId,
    )

    if (!dependencies.userId) {
      // The user might have logged out since then.
      _friends.set([])
      return []
    }

    _friends.set(loadedFriends)
    initialized = true
    return loadedFriends
  }

  const _friends = writable<FriendWithProfile[]>([], () => {
    return $effect.root(() => {
      $effect(() => {
        dependencies.userId
        untrack(() => reload())
      })
    })
  })

  return {
    subscribe: _friends.subscribe,
    reload: () => reload(),

    remove: async (friendUid: string): Promise<ActionResponse> => {
      if (!dependencies.userId) return {}

      const friend = get(_friends).find((f) => f.friend_uid === friendUid)
      if (!friend) return {}

      // Remove the friend immediately from the list. We assume it's going to go
      // through.
      _friends.update((friends) => friends.filter((f) => f !== friend))

      try {
        const { error } = await dependencies.supabase
          .from('friends')
          .delete()
          .eq('id', friend.id)
        if (error) throw error.message
        return {}
      } catch (e) {
        _friends.update((friends) => [...friends, friend])
        return { error: `${e}` }
      }
    },

    add: async (friendUid: string): Promise<ActionResponse> => {
      if (!dependencies.userId) return {}

      const existingFriend = get(_friends).find(
        (f) => f.friend_uid === friendUid,
      )
      if (existingFriend) return {}

      const { error } = await dependencies.supabase.from('friends').insert({
        user_uid: dependencies.userId,
        friend_uid: friendUid,
        notifications: false,
      })

      if (error) return { error: error.message }

      await reload()

      reloadNewAchievements()

      return {}
    },

    switchNotifications: async (
      friendUid: string,
      value: boolean | undefined = undefined,
    ): Promise<ActionResponse> => {
      if (!dependencies.userId) return {}

      const friend = get(_friends).find((f) => f.friend_uid === friendUid)
      if (!friend) return {}

      const originalNotifications = friend.notifications
      const newNotifications = value ?? !originalNotifications
      // Change the state immediately. We assume it's going to go
      // through.
      friend.notifications = newNotifications
      _friends.update((friends) => friends)

      try {
        const { error } = await dependencies.supabase
          .from('friends')
          .update({ notifications: newNotifications })
          .eq('id', friend.id)
        if (error) throw error.message
        return {}
      } catch (e) {
        friend.notifications = originalNotifications
        _friends.update((friends) => friends)
        return { error: `${e}` }
      }
    },
    get: async () => (initialized ? get(_friends) : reload()),
  }
}

const _loadFriends = async (
  supabase: TypedSupabaseClient,
  userId: string,
): Promise<FriendWithProfile[]> => {
  const { error, data } = await supabase
    .from('friends')
    .select('*, profile:profiles!friends_friend_uid_fkey (*)')
    .eq('user_uid', userId)
    .order('created_at', { ascending: false })

  if (error) console.error(error?.message ?? 'No friend data')
  return (data ?? []) as unknown as FriendWithProfile[]
}
